import React from 'react'
import ReactDOMServer from 'react-dom/server'
import { getDate, getMonth, getDaysInMonth } from 'date-fns'

import { store } from 'store/reducers'
import { Parse, Format } from 'services/Dates'
import { generateDateText } from 'services/Requests'
import { getUserName, getPositionName, hasAccessToThis, isMobile } from 'services/Utilities'
import { t } from 'translations'

export const getEventIcons = (
  {
    start,
    finalized,
    cancelled,
    contactListId,
    templateId,
    isAbsent,
    isLate,
    isLeavingEarly,
    isOnTimeoff,
    hasUnapprovedTimeoff,
    projects,
    comment,
    sce = [],
    arrival,
    leave,
    published,
    hasTimeoffComments
  },
  { employeeProjects = {}, birthDate } = {}
) => {
  const {
    settings: { projectRestrictions, showTemplateShiftIndicationInSchedule, hideConfirmedShiftInScheduler, hidePublishedStatusInScheduler }
  } = store.getState()

  const icons = []

  // Show if the shift has been published
  if (published && !hidePublishedStatusInScheduler) icons.push('fa-check-square')

  // Schedule confirmation events
  const lastConfirmation = [...sce].shift()
  if (
    typeof lastConfirmation === 'object' &&
    !hideConfirmedShiftInScheduler &&
    lastConfirmation.type !== 'cancellation' &&
    hasAccessToThis('scheduleConfirm')
  ) {
    switch (lastConfirmation.method) {
      case 'email':
        if (lastConfirmation.events.confirmed) icons.push('fa-user-check')
        else if (lastConfirmation.events.opened) icons.push('fa-envelope-open')
        else if (lastConfirmation.events.delivered) icons.push('fa-envelope')
        else if (lastConfirmation.events.failed) icons.push('fa-envelope red-text')
        else if (lastConfirmation.events.accepted) icons.push('fa-envelope')
        break
      case 'phone':
        if (lastConfirmation.events.noPhone) icons.push('fa-mobile red-text')
        else if (lastConfirmation.events.failed) icons.push('fa-mobile red-text')
        else if (lastConfirmation.events.confirmed) icons.push('fa-mobile')
        break
      default:
        break
    }
  }

  // Show if the shift belongs to a template
  if (showTemplateShiftIndicationInSchedule && templateId) icons.push('fa-calendar-plus')
  // Show if the user is on a project they are locked to
  if (projectRestrictions && Array.isArray(employeeProjects.locked) && employeeProjects.locked.includes(projects[0])) icons.push('fa-user-lock')

  // Basic icons
  if ((isOnTimeoff || hasUnapprovedTimeoff) && hasAccessToThis('manageTimeOff')) icons.push('fa-plane')
  if (isOnTimeoff && hasAccessToThis('manageTimeOff') && hasTimeoffComments) icons.push('fa-comment')
  if (isAbsent && hasAccessToThis('manageAttendance')) icons.push('fa-unlink')
  if (isLate && hasAccessToThis('manageAttendance')) icons.push('fa-alarm-clock')
  if (isLeavingEarly && hasAccessToThis('manageAttendance')) icons.push('fa-clock')
  if (finalized) icons.push('fa-lock')
  if (cancelled) icons.push('fa-ban')
  if (comment && comment.length) icons.push('fa-comment')
  if (contactListId) icons.push('fa-phone')

  // Show if today is the user's birthday
  if (birthDate) {
    const parsedBirthDay = Parse(birthDate)
    const today = Parse(start)
    let [birthDayOfMonth, birthMonth] = [getDate(parsedBirthDay), getMonth(parsedBirthDay)]
    let [todayDayOfMonth, todayMonth, daysInMonth] = [getDate(today), getMonth(today), getDaysInMonth(today)]
    // Handles people born on february 29th
    if (daysInMonth !== 29 && birthDayOfMonth === 29 && birthMonth === 1) birthDayOfMonth--

    if (birthDayOfMonth === todayDayOfMonth && birthMonth === todayMonth) icons.push('fa-birthday-cake')
  }

  // Punch icons
  if (hasAccessToThis('punchCardSee')) {
    if (leave) icons.push('fa-sign-out')
    else if (arrival) icons.push('fa-sign-in')
  }

  return icons.map((icon, index) => <i key={index} className={`fa ${icon} mr-1`}></i>)
}

export const getEventHtml = (event, { inTemplate } = {}) => {
  let {
    employeeId,
    positionId,
    projects,
    isAbsent,
    isLate,
    arrivalTime,
    isLeavingEarly,
    departureTime,
    isExtraShift,
    isOvertime,
    isOnCall,
    isIrreplaceable,
    needsReplacement,
    isOnTimeoff,
    hasUnapprovedTimeoff,
    noEmployeeAvailable,
    hasSpecificRequirements,
    coveredOtherUnit,
    time,
    start,
    end,
    tags = []
  } = event
  const {
    resources: { usersById, projectsById, tagsById },
    settings: { currentScheduleView, currentScheduleHierarchy, hideProjectCode },
    scheduleTemplates: { days: scheduleTemplateDays }
  } = store.getState()

  // All parts
  let employeeInfo,
    shiftInfo = [],
    projectInfo

  const positionInfo = getPositionName(positionId)

  // Tags
  const locations = tags.filter(tag => tagsById[tag]?.type === 'locations')

  const employeeAssigned = employeeId && usersById[employeeId] ? usersById[employeeId] : undefined

  if (employeeAssigned) {
    const { fullName, priorityNumber } = usersById[employeeId]
    employeeInfo = (
      <>
        {/* Priority number is optional and depends on a setting */}
        {priorityNumber ? `(${priorityNumber}) ` : ''}
        {fullName}
      </>
    )
  } else employeeInfo = t('manager.schedule.UNASSIGNED_SHIFT')

  // In user view, we show the position name rather than the user's name
  let shiftKeyInfo = currentScheduleHierarchy === 'users' ? positionInfo : employeeInfo

  // Adds the project code to the shift description
  if (projects && projects.length && !hideProjectCode) {
    const { code } = projectsById[projects[0]] || {}
    if (code) projectInfo = <span className="mr-1">[{code}]</span>
  }

  if (isAbsent && hasAccessToThis('manageAttendance')) shiftInfo.push(<b>{t('manager.schedule.IS_ABSENT')}</b>)
  if (isLate && hasAccessToThis('manageAttendance')) shiftInfo.push(<b>{t('manager.schedule.IS_ARRIVING_AT', { arrivalTime })}</b>)
  if (isLeavingEarly && hasAccessToThis('manageAttendance')) shiftInfo.push(<b>{t('manager.schedule.IS_LEAVING_AT', { departureTime })}</b>)
  if (isExtraShift) shiftInfo.push(t('manager.schedule.VARIABLE_END_TIME'))
  if (isOvertime) shiftInfo.push(t('manager.schedule.OVERTIME'))
  if (isOnCall) shiftInfo.push(t('manager.schedule.ON_CALL'))
  if (isIrreplaceable) shiftInfo.push(t('manager.schedule.IS_IRREPLACEABLE'))
  if (needsReplacement) shiftInfo.push(t('manager.schedule.NEEDS_REPLACEMENT'))
  if (noEmployeeAvailable) shiftInfo.push(t('manager.schedule.NO_EMPLOYEE_AVAILABLE'))
  if (isOnTimeoff && hasAccessToThis('manageTimeOff')) shiftInfo.push(t('manager.schedule.IN_TIME_OFF'))
  if (hasUnapprovedTimeoff && hasAccessToThis('manageTimeOff')) shiftInfo.push(t('manager.schedule.UNAPPROVED_TIME_OFF'))
  if (hasSpecificRequirements && hasAccessToThis('userManageSpecificRestrictions')) shiftInfo.push(t('manager.schedule.HAS_SPECIFIC_REQUIREMENTS'))
  if (coveredOtherUnit) shiftInfo.push(t('manager.schedule.COVEREDOTHERUNIT'))

  // Handles case where time field does not exist (like schedule templates)
  if (!time) time = `${Format(start, { outFormat: 'time' })} - ${Format(end, { outFormat: 'time' })}`

  const dailyEvent = (
    <>
      {getEventIcons(event, employeeAssigned)}
      {projectInfo}
      <b>{time}</b>
      <span className="ml-1">{shiftKeyInfo}</span>
      {shiftInfo.map((info, index) => (
        <span key={index}>
          <span className="mx-1">/</span>
          {info}
        </span>
      ))}
    </>
  )

  const weeklyEvent = (
    <>
      <div>
        {getEventIcons(event, employeeAssigned)}
        {shiftKeyInfo}
      </div>
      {projectInfo}
      <b>{time}</b>
      {shiftInfo.map((info, index) => (
        <span key={index}>
          <span className="mx-1">/</span>
          {info}
        </span>
      ))}
      {/* Locations - Only visible on mobile and smaller screens*/}
      {locations.map(location => {
        const { icon, name } = tagsById[location]

        return (
          <div key={location} className="d-xl-none">
            <i className={`fa ${icon} mr-1`} />
            <b>{name}</b>
          </div>
        )
      })}
    </>
  )

  const useWeekly = inTemplate ? scheduleTemplateDays > 1 : currentScheduleView === 'weekly' || isMobile()

  // Daypilot does not support jsx except in specific cases and it is cleaner and less error prone to use jsx than build an html string manually.
  return ReactDOMServer.renderToString(useWeekly ? weeklyEvent : dailyEvent)
}

export const getTimeOffHtml = ({ start, end, userId, sce, comments }) => {
  const hasTimeoffComments = comments?.length > 0
  const {
    settings: { currentScheduleView }
  } = store.getState()
  const useWeekly = currentScheduleView === 'weekly' || isMobile()

  const icons = getEventIcons({ sce, isOnTimeoff: true, hasTimeoffComments })
  const time = generateDateText(start, end, { partialOnlyTime: true })
  const userName = getUserName(userId)

  const dailyTimeOff = (
    <>
      {icons}
      <b>{time}</b>
      <span className="ml-1">{userName}</span>
    </>
  )

  const weeklyTimeOff = (
    <>
      <div>
        {icons}
        {userName}
      </div>
      <b>{time}</b>
    </>
  )

  return ReactDOMServer.renderToString(useWeekly ? weeklyTimeOff : dailyTimeOff)
}

export const getAbsenceHtml = ({ start, end, userId, comments }) => {
  const hasComments = comments?.length > 0
  const {
    settings: { currentScheduleView }
  } = store.getState()
  const useWeekly = currentScheduleView === 'weekly' || isMobile()

  const time = generateDateText(start, end, { partialOnlyTime: true })
  const userName = getUserName(userId)

  const dailyAbsence = (
    <>
      <b>{time}</b>
      <span className="ml-1">{userName}</span>
    </>
  )

  const weeklyAbsence = (
    <>
      <div>
        {hasComments ? <i className="fas fa-comment mr-1" /> : null}
        {userName}
      </div>
      <b>{time}</b>
    </>
  )

  return ReactDOMServer.renderToString(useWeekly ? weeklyAbsence : dailyAbsence)
}
